import { createContext, ReactNode, useContext, useState } from "react";
import { createReferenceNumbers } from "./api";
import { useFlashMessage } from "./FlashMessageProvider";

type ReferenceNumberContext = {
  base: string;
  referenceNumbers: string[];
  generateReferenceNumbers: (
    basePart: string,
    amount: number,
    type: "fi" | "rf",
    fiBase: boolean
  ) => Promise<void>;
  error: Error | null;
  loading: boolean;
};

const referenceNumberCtx = createContext<ReferenceNumberContext | null>(null);
const Provider = referenceNumberCtx.Provider;

type ReferenceNumberProviderProps = {
  children: ReactNode;
};

export const ReferenceNumberProvider = ({
  children,
}: ReferenceNumberProviderProps) => {
  const [referenceNumbers, setReferenceNumbers] = useState<string[]>([]);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);
  const [base, setBase] = useState("");
  const showMessage = useFlashMessage();

  const generateReferenceNumbers = (
    basePart: string,
    amount: number,
    type: string,
    fiBase: boolean
  ) => {
    setLoading(true);
    setError(null);
    setBase(basePart);
    return createReferenceNumbers(basePart, amount, type, fiBase)
      .then((res) => {
        setReferenceNumbers(res);
        showMessage({
          id: "referencesCreated",
          values: { amount: amount.toString() },
        });
      })
      .catch(setError)
      .finally(() => setLoading(false));
  };

  return (
    <Provider
      value={{
        base,
        referenceNumbers,
        generateReferenceNumbers,
        error,
        loading,
      }}
    >
      {children}
    </Provider>
  );
};

export const useReferenceNumbers = () => {
  const context = useContext(referenceNumberCtx);
  if (!context) {
    throw Error(
      "ReferenceNumberContext must be used inside ReferenceNumberProvider"
    );
  }
  return context;
};
